import React from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../constants';
import FadeImage from '../../shared/FadeImage';

/**
 * SkillSupportList — Lista compacta de support cards que enseñan la skill.
 * Usa el mismo patrón de icono y overlay que SupportDisplay.js.
 * Props: supports = array de { support_id, rarity, Support_Type, support_name_en, char_name_en, char_id }
 */

const getTypeIconPath = (supportType) => {
    if (!supportType) return '';
    const type = supportType.charAt(0).toUpperCase() + supportType.slice(1).toLowerCase();
    if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
        return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
    }
    if (['Pal', 'Group'].includes(type)) {
        return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
    }
    return '';
};

const RARITY_COLORS = {
    'R': 'border-gray-300 dark:border-gray-500',
    'SR': 'border-blue-400 dark:border-blue-500',
    'SSR': 'border-yellow-400 dark:border-yellow-500',
};
const RARITY_TEXT = {
    'R': 'text-gray-500 dark:text-gray-400',
    'SR': 'text-blue-600 dark:text-blue-400',
    'SSR': 'text-yellow-600 dark:text-yellow-400',
};

const SupportItem = ({ support }) => {
    const iconPath = `${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${support.support_id}_Icon.png&width=128`;
    const typeIconPath = getTypeIconPath(support.Support_Type);
    const rarityBorder = RARITY_COLORS[support.rarity] || 'border-gray-300';
    const rarityText = RARITY_TEXT[support.rarity] || 'text-gray-500';

    return (
        <Link
            to={`/characters/support/${support.support_id}`}
            className="flex flex-col items-center gap-1.5 group w-[72px]"
            title={`${support.char_name_en} — ${support.support_name_en}`}
        >
            {/* Icono de support con overlay de tipo — igual que SupportDisplay.js */}
            <div className={`relative w-14 h-14 rounded-xl overflow-hidden border-2
                ${rarityBorder}
                group-hover:scale-105 transition-transform duration-200 shadow-sm`}>
                <FadeImage
                    src={iconPath}
                    alt={support.support_name_en}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
                {/* Type icon overlay bottom-right */}
                {typeIconPath && (
                    <img
                        src={typeIconPath}
                        alt={support.Support_Type}
                        className="absolute bottom-0 right-0 w-5 h-5 object-contain z-10"
                    />
                )}
            </div>

            {/* Nombre del personaje */}
            <p className="text-[10px] font-semibold text-center text-gray-700 dark:text-gray-300
                group-hover:text-blue-600 dark:group-hover:text-blue-400
                transition-colors leading-tight line-clamp-2 w-full">
                {support.char_name_en}
            </p>

            {/* Rareza */}
            {support.rarity && (
                <span className={`text-[9px] font-bold ${rarityText}`}>
                    {support.rarity}
                </span>
            )}
        </Link>
    );
};

const SkillSupportList = ({ supports }) => {
    if (!supports || supports.length === 0) return null;

    return (
        <div className="space-y-3">
            {/* Header de sección */}
            <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-2">
                    Taught by Support Cards ({supports.length})
                </span>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Grid wrap — se expande verticalmente sin scroll horizontal */}
            <div className="flex flex-wrap gap-4">
                {supports.map((s) => (
                    <SupportItem key={s.support_id} support={s} />
                ))}
            </div>
        </div>
    );
};

export default SkillSupportList;
